import {
  ColumnDef,
  ColumnResizeDirection,
  ColumnResizeMode,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useReducer, useState } from "react";
import { HeaderColumns } from "./blocks/HeaderColumns";
import { HeaderColumn } from "./blocks/HeaderColumn";
import { TableRow } from "./blocks/TableRow";
import { TableCell } from "./blocks/TableCell";
import { ColumnResizer } from "./blocks/ColumnResizer";
import { TableContainer } from "./blocks/TableContainer";
import { TableHeader } from "./blocks/TableHeader";
import { TableBody } from "./blocks/TableBody";

export type ShortsworksTable<T> = {
  id: string;
  data: T[];
  // 타입 맞지 않는 점 확인 필요
  columns: ColumnDef<T>[] | any;
  onClickCell?: (tableId: string, cell: { id: string; value: any }) => void;
  enableRowSelection?: boolean;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  rowIdKey?: string;
  rowSelection?: RowSelectionState;
};

export const ShortsworksTable = <T,>({
  id,
  data,
  columns: inputColumns,
  onClickCell,
  enableRowSelection,
  onRowSelectionChange,
  rowIdKey,
  rowSelection = {},
}: ShortsworksTable<T>) => {
  const rerender = useReducer(() => ({}), {})[1];
  const [columnResizeMode, setColumnResizeMode] =
    useState<ColumnResizeMode>("onChange");
  const [columnResizeDirection, setColumnResizeDirection] =
    useState<ColumnResizeDirection>("ltr");
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo<ColumnDef<T>[]>(() => inputColumns, [inputColumns]);

  const table = useReactTable({
    columns,
    data,
    debugTable: false,
    enableMultiRowSelection: true,
    enableRowSelection: enableRowSelection ?? false,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), //client-side sorting
    // @ts-ignore
    getRowId: (row) => (rowIdKey ? row[rowIdKey] : row.uuid),
    onSortingChange: setSorting, //optionally control sorting state in your own scope for easy access
    state: {
      sorting,
      rowSelection,
    },
    columnResizeMode,
    columnResizeDirection,
  });

  return (
    <div
      className="h-full"
      style={{ direction: table.options.columnResizeDirection }}
    >
      <TableContainer style={{ width: table.getTotalSize() }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <HeaderColumns key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <HeaderColumn
                  isFirst={index === 0}
                  header={header}
                  key={header.id}
                  style={{ left: header.getStart(), width: header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  <ColumnResizer
                    onDoubleClick={() => header.column.resetSize()}
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    isResizing={header.column.getIsResizing()}
                  />
                </HeaderColumn>
              ))}
            </HeaderColumns>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} row={row}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    left: cell.column.getStart(),
                    width: cell.column.getSize(),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableContainer>
    </div>
  );
};
